/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx } from '@compiled/react';

import AssetsObjectIcon from '@atlaskit/icon-lab/core/assets-object';
import AssetsObjectTypeIcon from '@atlaskit/icon-lab/core/assets-object-type';
import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import BoardIcon from '@atlaskit/icon/core/board';
import CommentIcon from '@atlaskit/icon/core/comment';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import FolderIcon from '@atlaskit/icon/core/folder-open';
import GoalIcon from '@atlaskit/icon/core/goal';
import IncidentIcon from '@atlaskit/icon/core/incident';
import ProjectIcon from '@atlaskit/icon/core/project';
import PlanIcon from '@atlaskit/icon/core/roadmap';
import EmbedIcon from '@atlaskit/icon/core/smart-link-embed';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import Image from '@atlaskit/image';
import BlogIcon from '@atlaskit/object/blog';
import CalendarIcon from '@atlaskit/object/calendar';
import DatabaseIcon from '@atlaskit/object/database';
import PageIcon from '@atlaskit/object/page';
import PageLiveIcon from '@atlaskit/object/page-live-doc';
import QuestionIcon from '@atlaskit/object/question';
import WhiteboardIcon from '@atlaskit/object/whiteboard';
import GenericTile from '@atlaskit/tile';
import { type SearchResultType } from '@atlassian/search-client';

import { ProductKeys } from '../../constants/products';
import { ProductIcon } from '../product-icon';

type SizeType = 'small' | 'medium';

const isImageDataUrl = (str: string) => /^data:image\/[^;]+;base64,/.test(str);

/**
 * Returns the appropriate icon for a Jira item based on the provided parameters.
 *
 * @param {string | null | undefined} iconUrl - The URL of the icon image.
 * @param {SearchResultType | string} type - The type of the search result (e.g., 'board', 'plan', etc.).
 * @param {boolean} isIssue - Flag indicating if the item is an issue.
 * @param {'small' | 'medium'} [size='small'] - The size of the icon ('small' or 'medium').
 * @returns {React.JSX.Element} The JSX element representing the icon.
 */

export const getJiraEntityIcon = ({
	iconUrl,
	type,
	isIssue,
	size = 'small',
}: {
	iconUrl: string | null | undefined;
	type: SearchResultType | string;
	isIssue: boolean;
	size?: SizeType;
}): React.JSX.Element => {
	const imageSize = size === 'medium' ? 22 : 16;

	// Use the iconUrl if available
	if (iconUrl) {
		if (isIssue) {
			// wrapper is only needed for issues. Other types don't need the background and padding with iconUrl.
			return (
				<GenericTile label="Work item icon" size={size}>
					{/* eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details */}
					<Image
						alt="Work item icon"
						testId="jira-entity-icon"
						width={imageSize}
						height={imageSize}
						src={iconUrl}
					/>
				</GenericTile>
			);
		}

		return (
			// The image covers up the whole wrapper
			<GenericTile isInset={false} label={`${type} icon`} size={size}>
				{/* eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details */}
				<Image alt={`${type} icon`} testId="jira-entity-icon" src={iconUrl} />
			</GenericTile>
		);
	}

	if (isIssue) {
		return (
			<GenericTile label="Work item icon" size={size}>
				<WorkItemIcon spacing="none" size={size} label="work-item-icon" />
			</GenericTile>
		);
	}

	let icon: React.JSX.Element;
	switch (type) {
		case 'board':
			icon = <BoardIcon spacing="none" size={size} label="board-icon" />;
			break;
		case 'plan':
			icon = <PlanIcon spacing="none" size={size} label="plan-icon" />;
			break;
		case 'filter':
			icon = <FilterIcon spacing="none" size={size} label="filter-icon" />;
			break;
		case 'dashboard':
			icon = <DashboardIcon spacing="none" size={size} label="dashboard-icon" />;
			break;
		case 'project':
			icon = <SpacesIcon spacing="none" size={size} label="space-icon" />;
			break;
		case 'incident':
			icon = <IncidentIcon spacing="none" size={size} label="incident-icon" />;
			break;

		default:
			// This never happens
			return <ProductIcon size={size} product={ProductKeys.Jira} />;
	}

	return (
		<GenericTile label={`${type} icon`} size={size}>
			{icon}
		</GenericTile>
	);
};

/**
 * Returns the appropriate icon for a Confluence item based on the provided parameters.
 *
 * @param {string | null | undefined} iconUrl - The URL of the icon image.
 * @param {SearchResultType | string} type - The type of the search result (e.g., 'space', 'page', etc.).
 * @param {'small' | 'medium'} [size='small'] - The size of the icon ('small' or 'medium').
 * @param {boolean} isLiveDoc - Flag indicating if the page is a live document.
 * @returns {React.JSX.Element} The JSX element representing the icon.
 */
export const getConfluenceEntityIcon = ({
	iconUrl,
	type,
	size = 'small',
	isLiveDoc = false,
}: {
	iconUrl: string | null | undefined;
	type: SearchResultType | string;
	size?: SizeType;
	isLiveDoc?: boolean;
}): React.JSX.Element => {
	try {
		// Use the iconUrl if available, the iconUrl for Confluence entities is usually an absolute URL
		// We only use custom iconUrl for Spaces
		if (type === 'space' && iconUrl && (isImageDataUrl(iconUrl) || new URL(iconUrl))) {
			return (
				<GenericTile isInset={false} label={`${type} icon`} size={size}>
					{/* eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details */}
					<Image src={iconUrl} />
				</GenericTile>
			);
		}
	} catch {}

	/*
	 ** For finding all the Confluence entities look at platform/packages/search/search-page/src/common/utils/filters/constants.ts -> typeFilter1PMapping
	 * */
	let icon: React.JSX.Element;

	switch (type) {
		case 'space':
			// spaces usually have an iconUrl, but if not we fall back to the project icon
			icon = <ProjectIcon size={size} label="space icon" />;
			break;
		case 'page':
			if (isLiveDoc) {
				icon = <PageLiveIcon size={size} label="live page icon" />;
			} else {
				icon = <PageIcon size={size} label="page icon" />;
			}
			break;
		case 'blogpost':
			icon = <BlogIcon size={size} label="blogpost icon" />;
			break;
		case 'attachment':
			icon = <AttachmentIcon spacing="none" size={size} label="attachment icon" />;
			break;
		case 'comment':
			icon = <CommentIcon spacing="none" size={size} label="comment icon" />;
			break;
		case 'whiteboard':
			icon = <WhiteboardIcon size={size} label="whiteboard icon" />;
			break;
		case 'database':
			icon = <DatabaseIcon size={size} label="database icon" />;
			break;
		case 'question':
			icon = <QuestionIcon size={size} label="question icon" />;
			break;
		case 'calendar':
			icon = <CalendarIcon size={size} label="calendar icon" />;
			break;
		case 'embed':
			icon = <EmbedIcon spacing="none" size={size} label="embed icon" />;
			break;
		case 'folder':
			icon = <FolderIcon spacing="none" size={size} label="folder icon" />;
			break;

		default:
			// fallback to product icon. It has no wrapper
			return <ProductIcon label="confluence icon" size={size} product={ProductKeys.Confluence} />;
	}

	return (
		<GenericTile label={`${type} icon`} size={size}>
			{icon}
		</GenericTile>
	);
};

/**
 * Returns the appropriate icon for an Atlas item based on the provided parameters.
 *
 * @param {string | null | undefined} iconUrl - The URL of the icon image.
 * @param {SearchResultType | string} type - The type of the search result (e.g., 'goal', 'project', etc.).
 * @param {'small' | 'medium'} [size='small'] - The size of the icon ('small' or 'medium').
 * @returns {React.JSX.Element} The JSX element representing the icon.
 */
export const getAtlasEntityIcon = ({
	iconUrl,
	type,
	size = 'small',
}: {
	iconUrl: string | null | undefined;
	type: SearchResultType | string;
	size?: SizeType;
}): React.JSX.Element => {
	try {
		// Use the iconUrl if available, the iconUrl for Atlas entities is usually an absolute URL
		if (iconUrl && (isImageDataUrl(iconUrl) || new URL(iconUrl))) {
			return (
				<GenericTile isInset={false} label={`${type} icon`} size={size}>
					{/* eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details */}
					<Image src={iconUrl} />
				</GenericTile>
			);
		}
	} catch {}

	let icon: React.JSX.Element;
	switch (type) {
		case 'goal':
			icon = <GoalIcon size={size} label="goal icon" />;
			break;
		case 'project':
			icon = <ProjectIcon size={size} label="project icon" />;
			break;

		default:
			// fallback to product icon. It has no wrapper
			return <ProductIcon label="Atlas icon" size={size} product={ProductKeys.Atlas} />;
	}

	return (
		<GenericTile label={`${type} icon`} size={size}>
			{icon}
		</GenericTile>
	);
};

/**
 * Returns the appropriate icon for a Jira item based on the provided parameters.
 *
 * @param {string | null | undefined} iconUrl - The URL of the icon image.
 * @param {SearchResultType | string} type - The type of the search result (e.g., 'board', 'plan', etc.).
 * @param {boolean} isIssue - Flag indicating if the item is an issue.
 * @param {'small' | 'medium'} [size='small'] - The size of the icon ('small' or 'medium').
 * @returns {React.JSX.Element} The JSX element representing the icon.
 */

export const getAssetsEntityIcon = ({
	iconUrl,
	type,
	size = 'small',
}: {
	iconUrl?: string | null;
	type: SearchResultType | string;
	size?: SizeType;
}): React.JSX.Element => {
	// Use the iconUrl if available
	if (iconUrl) {
		return (
			// The image covers up the whole wrapper
			<GenericTile label={`${type} icon`} size={size}>
				<Image alt="" testId="assets-entity-icon" src={iconUrl} />
			</GenericTile>
		);
	}

	let icon: React.JSX.Element;
	switch (type) {
		case 'schema':
			icon = <AssetsSchemaIcon spacing="none" size={size} label="schema-icon" />;
			break;
		case 'type':
			icon = <AssetsObjectTypeIcon spacing="none" size={size} label="type-icon" />;
			break;
		case 'object':
			icon = <AssetsObjectIcon spacing="none" size={size} label="object-icon" />;
			break;
		default:
			// This never happens
			return <ProductIcon size={size} product={ProductKeys.Assets} />;
	}

	return (
		<GenericTile label={`${type} icon`} size={size}>
			{icon}
		</GenericTile>
	);
};
